'use client';

import React, { useState, forwardRef } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faClose, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';

export type SearchVariant = 'outlined' | 'solid' | 'neutral';
export type SearchSize = 'sm' | 'md' | 'lg';

export interface SearchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: SearchSize;
  placeholder?: string;
  variant?: SearchVariant;
  className?: string;
  sx?: React.CSSProperties;
  onSearch: (value: string) => void;
  slotProps?: {
    searchIcon?: React.ReactNode;
    clearIcon?: React.ReactNode;
    inputStyle?: React.CSSProperties;
    iconClassName?: string;
  };
}

const getContainerClasses = (
  variant: SearchVariant,
  size: SearchSize,
  focused: boolean,
  disabled: boolean | undefined,
  className?: string
) => {
  const baseClasses =
    'flex items-center rounded-lg transition-all duration-200';

  const variantClasses = {
    outlined: [
      'border border-neutral-200',
      focused ? 'border-primary-500' : 'hover:border-neutral-500',
      disabled ? 'border-neutral-100' : '',
    ].join(' '),
    solid: [
      'bg-gray-100',
      focused ? 'bg-gray-100/80' : 'hover:bg-gray-100/90',
      disabled ? 'bg-gray-50' : '',
    ].join(' '),
    neutral: [
      'bg-neutral-100',
      focused ? 'bg-neutral-100/80' : 'hover:bg-neutral-100/90',
      disabled ? 'bg-neutral-50' : '',
    ].join(' '),
  };

  const sizeClasses = {
    sm: 'min-h-8 text-xs',
    md: 'min-h-10 text-sm',
    lg: 'min-h-12 text-base',
    xl: 'min-h-14 text-lg',
  };

  return twMerge(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    className
  );
};

const getInputClasses = (disabled: boolean | undefined) => {
  return twMerge(
    'border-none focus:outline-none w-full bg-transparent',
    disabled ? 'cursor-not-allowed text-neutral-300' : 'text-neutral-900'
  );
};

const getIconClasses = (
  size: SearchSize,
  disabled: boolean | undefined,
  visible: boolean,
  iconClassName?: string
) => {
  const baseClasses = 'text-neutral-400';

  const sizeClasses = {
    sm: 'h-4',
    md: 'h-5',
    lg: 'h-6',
  };

  const visibilityClasses = visible ? 'opacity-100' : 'opacity-0';
  const stateClasses = disabled ? '' : 'hover:text-neutral-500 cursor-pointer';

  return twMerge(
    baseClasses,
    sizeClasses[size],
    stateClasses,
    visibilityClasses,
    iconClassName
  );
};

const getPaddingClasses = (size: SearchSize) => {
  const paddingClasses = {
    sm: 'py-2',
    md: 'py-2',
    lg: 'py-4',
  };

  return paddingClasses[size];
};

const getIconContainerClasses = (
  size: SearchSize,
  position: 'start' | 'end'
) => {
  const baseClasses = 'flex items-center';

  const positionClasses = {
    start: {
      sm: 'pl-3 pr-2',
      md: 'pl-4 pr-2',
      lg: 'pl-5 pr-4',
    },
    end: {
      sm: 'pr-3 pl-2',
      md: 'pr-4 pl-2',
      lg: 'pr-5 pl-4',
    },
  };

  return twMerge(baseClasses, positionClasses[position][size]);
};

const Search = forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      onSearch,
      size = 'md',
      placeholder = 'Search...',
      variant = 'outlined',
      className,
      sx,
      slotProps,
      disabled,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(
      props.defaultValue?.toString() || ''
    );

    // Controlled vs uncontrolled input handling
    const value = props.value !== undefined ? props.value : internalValue;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      props.onBlur?.(e);
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;

      const newValue = e.target.value;
      setInternalValue(newValue);
      onSearch(newValue);
    };

    const handleClear = () => {
      if (disabled) return;

      setInternalValue('');
      onSearch('');

      // If the input is controlled, we need to notify
      if (props.onChange) {
        const event = {
          target: { value: '' },
        } as React.ChangeEvent<HTMLInputElement>;
        props.onChange(event);
      }
    };

    return (
      <div
        className={getContainerClasses(
          variant,
          size,
          focused,
          disabled,
          className
        )}
        style={sx}
      >
        <div
          className={twMerge(
            'flex items-center w-full',
            getPaddingClasses(size)
          )}
        >
          <div className={getIconContainerClasses(size, 'start')}>
            {slotProps?.searchIcon || (
              <Icon
                icon={faSearch}
                size={size === 'sm' ? 'sm' : 'lg'}
                className={getIconClasses(
                  size,
                  disabled,
                  true,
                  slotProps?.iconClassName
                )}
              />
            )}
          </div>
          <input
            ref={ref}
            type="text"
            className={getInputClasses(disabled)}
            placeholder={placeholder}
            onChange={handleSearch}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={slotProps?.inputStyle}
            disabled={disabled}
            value={value}
            {...props}
          />
          <div className={getIconContainerClasses(size, 'end')}>
            {slotProps?.clearIcon || (
              <Icon
                icon={faClose}
                className={getIconClasses(
                  size,
                  disabled,
                  !!value,
                  slotProps?.iconClassName
                )}
                onClick={disabled ? undefined : handleClear}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);

Search.displayName = 'Search';

export default Search;
